import { supabaseClient } from '@/services/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { isNonNullable } from './nullability-helpers';
import type { PossibleLanguage, TranslationKey } from '@/i18n';
import { FrontendError } from './error-helpers';
import type { FrontendScenarioDefinition } from '@shared-types/frontend-scenario-definition';
import type { PeriodKey, PublicScenarioDetails } from '@shared-types/scenario-definition';
import type { FrontendSimulationMetadata } from '@/types/simulation';
import type { CompetencyFramework } from '@shared-types/competencies';
import type { ChatMessage } from '@shared-types/chatbotAI';
import type { EventRowInsert } from '@shared-types/events.types';
export class EdgeFunctionsManager {
    static getParticipantSignInData = (
        accessCode: string,
    ): Promise<{ email: string; otp: string; simulationID: string }> =>
        this.invokeEdgeFunction('get-participant-sign-in-data', { accessCode });

    static requestAccessLink = (
        email: string,
        language: PossibleLanguage,
        name?: string,
        organization?: string,
    ): Promise<string> => this.invokeEdgeFunction('request-access-link', { email, name, organization, language });

    static createDemoSimulation = async (): Promise<{ id: string }> =>
        this.invokeEdgeFunction('create-demo-simulation');

    static createSimulation = async (
        simulationMetadata: FrontendSimulationMetadata,
        organizationId: string,
    ): Promise<{ id: string }> => this.invokeEdgeFunction('create-simulation', { simulationMetadata, organizationId });

    static editSimulation = async (
        simulationID: string,
        simulationMetadata: FrontendSimulationMetadata,
    ): Promise<void> => this.invokeEdgeFunction('edit-simulation', { simulationID, simulationMetadata });

    static calculateNextPeriod = (simulationID: string, currentPeriodKey: PeriodKey): Promise<void> =>
        this.invokeEdgeFunction('calculate-next-period', { simulationID, currentPeriodKey });

    static startPeriod = (simulationID: string): Promise<void> =>
        this.invokeEdgeFunction('start-period', { simulationID });

    static sendParticipantInviteEmails = async (simulationID: string, language: PossibleLanguage, sendToAll: boolean) =>
        this.invokeEdgeFunction('send-invite-emails', { simulationID, sendToAll, language });

    static sendPasswordResetEmail = (email: string, language: PossibleLanguage): Promise<void> =>
        this.invokeEdgeFunction('send-reset-password-email', { email, language });

    static changeEmail = (email: string, newEmail: string, language: PossibleLanguage): Promise<void> =>
        this.invokeEdgeFunction('send-change-email-email', { email, newEmail, language });

    static createSimulationKnowledge = (simulationID: string): Promise<void> =>
        this.invokeEdgeFunction('create-simulation-knowledge', { simulationID });

    static getPossibleScenarios = (): Promise<PublicScenarioDetails[]> =>
        this.invokeEdgeFunction('get-possible-scenarios');

    static getScenarioDefinition = (
        body: { simulationID: string; language?: string } | { scenarioID: string; language?: string },
    ): Promise<FrontendScenarioDefinition> => this.invokeEdgeFunction('get-scenario-definition', body);

    static generateAIConversation = (messages: ChatMessage[], json_schema: string): Promise<any> =>
        this.invokeEdgeFunction('generate-ai-conversation', { messages, json_schema });

    static generateAIContent = (body: {
        promptData: Record<string, string | undefined>;
        promptFilePaths: string[];
        sourceDocuments: string[];
        json_schema: string;
        organizationId: string;
        params?: Record<string, any>;
    }): Promise<any> => this.invokeEdgeFunction('generate-ai-content', body);

    static transcribeAudio = (formData: FormData): Promise<any> =>
        this.invokeEdgeFunction('transcribe-audio', formData);

    static generateAudio = (formData: FormData): Promise<any> => this.invokeEdgeFunction('text-to-speech', formData);

    static getCompetencyFramework = (frameworkId: string): Promise<CompetencyFramework> =>
        this.invokeEdgeFunction('get-competency-framework', { frameworkId });

    static generateChallenges = (contentFile: string): Promise<void> =>
        this.invokeEdgeFunction('generate-challenges', { contentFile });

    static verifyJwt = (jwt: string): Promise<{ token: string; email: string; simulationId: string }> =>
        this.invokeEdgeFunction('verify-jwt', { jwt });

    static startTask = (courseId: string): Promise<void> => this.invokeEdgeFunction('start-task', { courseId });

    static submitTask = (taskAttemptId: string): Promise<void> =>
        this.invokeEdgeFunction('submit-task', { taskAttemptId });

    static resetUserTaskAttempts = (organizationId: string, departmentId?: string): Promise<void> =>
        this.invokeEdgeFunction('reset-user-task-attempts', { organizationId, departmentId });

    static addEvent = (event: EventRowInsert): Promise<void> => this.invokeEdgeFunction('add-event', { event });

    static invokeEdgeFunction = async (name: string, body?: Record<string, any>) => {
        const { data, error } = await supabaseClient.functions.invoke(name, { body });
        if (isNonNullable(error)) {
            if (error instanceof FunctionsHttpError) {
                const {
                    error: { code },
                } = (await error.context.json()) as { error: { code: string } };
                throw new FrontendError(`errors.edgeFunctions.${code}` as TranslationKey);
            } else {
                throw error;
            }
        }
        return data;
    };
}
